'use client';

import { Fragment, useEffect, useRef, useState } from 'react';
import { HelpCircle, Home, LogOut, Mail, Menu, Search, X } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { mutate } from 'swr';

import { LogoutDialog } from '@/components/auth/logout-dialog';
import { Brand } from '@/components/Brand';
import HeaderTooltip from '@/components/header-tooltip';
import NotificationBadge from '@/components/notifications/notification-badge';
import NotificationPopoverButton from '@/components/notifications/notification-popover-button';
import NotificationProvider from '@/components/notifications/notification-provider';
import { MobileSearchMenu } from '@/components/search/mobile-search-menu';
import { Button } from '@/components/ui/button';
import UserMenu from '@/components/user-menu';
import { apiRoutes, getApiUrl } from '@/constants/api';
import { getDashboardNavItems } from '@/constants/dashboard-nav';
import { useAuth } from '@/contexts/auth-context';
import { useIsMobile } from '@/hooks/use-mobile';
import { markUnreadMessageNotificationsAsRead } from '@/lib/api/notification';

import Container from './container';
import HeaderDashboardSkeleton from './header-dashboard-skeleton';
import SearchSection from './header-search-section';

export default function HeaderDashboard() {
  const { user, isLoading } = useAuth();
  const pathname = usePathname();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);
  const [isMobileSearchFilterOpen, setIsMobileSearchFilterOpen] =
    useState(false);

  const invertAdmin = pathname.startsWith('/admin');
  const navItems = getDashboardNavItems(
    user?.username || '',
    user?.role,
    invertAdmin,
  );

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    closeMobileMenu();
  };

  const markMessageNotificationsAsRead = () => {
    void (async () => {
      try {
        await markUnreadMessageNotificationsAsRead();
      } catch (error) {
        console.error('Failed to mark message notifications as read', error);
      } finally {
        await mutate(getApiUrl(apiRoutes.notification.getUnreadCount));
        await mutate((key) => {
          if (typeof key !== 'string') {
            return false;
          }

          return key.startsWith(getApiUrl(apiRoutes.notification.getAll()));
        });
      }
    })();
  };

  useEffect(() => {
    if (!isMobile && isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile, isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobile && isMobileSearchOpen) {
      setIsMobileSearchOpen(false);
    }
  }, [isMobile, isMobileSearchOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen || isMobileSearchOpen) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    } else {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }

    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, [isMobileMenuOpen, isMobileSearchOpen]);

  // Focus trapping for mobile menu
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const closeButton = document.querySelector(
      '[aria-controls="mobile-menu"]',
    ) as HTMLElement;
    const mobileMenu = mobileMenuRef.current;
    if (!mobileMenu || !closeButton) return;

    const menuFocusableElements = mobileMenu.querySelectorAll(
      'a, button, [tabindex]:not([tabindex="-1"])',
    );

    const allFocusableElements = [
      closeButton,
      ...Array.from(menuFocusableElements),
    ];
    const firstElement = allFocusableElements[0] as HTMLElement;
    const lastElement = allFocusableElements[
      allFocusableElements.length - 1
    ] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeMobileMenu();
      }
    };

    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

  if (isLoading) {
    return <HeaderDashboardSkeleton />;
  }

  if (!user) {
    return null;
  }

  return (
    <NotificationProvider user={user}>
      <header
        className='sticky top-0 z-50 flex h-dashboard-navbar-height-mobile items-center bg-white shadow-sm md:h-dashboard-navbar-height'
        role='banner'
      >
        <Container className='relative flex items-center justify-between gap-4 py-2'>
          <Brand href='/feed' onClick={closeMobileMenu} />

          {/* Desktop Navbar */}
          <nav
            className='hidden w-full items-center gap-2 md:flex'
            aria-label='Main navigation'
          >
            <SearchSection />

            <div
              className='flex items-center gap-3'
              role='group'
              aria-label='User actions'
            >
              <HeaderTooltip tooltipText='Feed'>
                <Button variant='header-icon' size='icon' asChild>
                  <Link href='/feed' aria-label='Go to feed'>
                    <Home />
                  </Link>
                </Button>
              </HeaderTooltip>

              <HeaderTooltip tooltipText='Notifications'>
                <NotificationPopoverButton />
              </HeaderTooltip>

              <HeaderTooltip tooltipText='Messages'>
                <Button variant='header-icon' size='icon' asChild>
                  <Link
                    href='/messages'
                    aria-label='Go to messages'
                    onClick={markMessageNotificationsAsRead}
                  >
                    <Mail className='text-brand-green' />
                    <NotificationBadge
                      className='absolute -right-1 -top-1'
                      type='messages'
                    />
                  </Link>
                </Button>
              </HeaderTooltip>

              <HeaderTooltip tooltipText='Support'>
                <Button variant='header-icon' size='icon' asChild>
                  <Link href='/contact' aria-label='Go to support'>
                    <HelpCircle />
                  </Link>
                </Button>
              </HeaderTooltip>

              <UserMenu className='ml-1' />
            </div>
          </nav>

          {/* Mobile Navbar */}
          <nav
            className='flex items-center gap-2 md:hidden'
            aria-label='Mobile navigation'
          >
            <button
              className='p-2'
              onClick={() => {
                setIsMobileSearchOpen(true);
                setIsMobileSearchFilterOpen(false);
                setIsMobileMenuOpen(false);
              }}
            >
              <Search className='size-6 text-brand-green' />
            </button>

            <div className='relative'>
              <NotificationPopoverButton />
            </div>

            <button
              onClick={() => {
                toggleMobileMenu();
                setIsMobileSearchOpen(false);
              }}
              aria-label={
                isMobileMenuOpen
                  ? 'Close navigation menu'
                  : 'Open navigation menu'
              }
              aria-expanded={isMobileMenuOpen}
              aria-controls='mobile-menu'
              type='button'
            >
              {isMobileMenuOpen ? (
                <X className='size-6 text-brand-green' />
              ) : (
                <Menu className='size-6 text-brand-green' />
              )}
            </button>
          </nav>
        </Container>
      </header>

      {isMobileMenuOpen && (
        <div
          className='fixed inset-x-0 bottom-0 top-dashboard-navbar-height-mobile z-50 border-t border-gray-200 md:hidden'
          role='dialog'
          aria-modal='true'
          aria-label='Navigation menu'
        >
          <div
            className='absolute inset-0 bg-[#414141]/80'
            onClick={closeMobileMenu}
            aria-hidden='true'
          />

          <div
            className='absolute inset-x-0 top-0 flex flex-col bg-white shadow-lg'
            id='mobile-menu'
            ref={mobileMenuRef}
          >
            <Container className='max-h-[calc(100vh-var(--dashboard-navbar-height-mobile))] overflow-y-auto'>
              <nav aria-label='Mobile navigation menu'>
                {navItems.map(({ label, href, icon: Icon }, index) => (
                  <Fragment key={`${label}-${index}`}>
                    <Link
                      href={href}
                      className='flex items-center gap-4 rounded-sm py-4 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2'
                      onClick={() => {
                        if (href === '/messages') {
                          markMessageNotificationsAsRead();
                        }
                        closeMobileMenu();
                      }}
                      tabIndex={0}
                    >
                      <Icon
                        className='size-6 text-brand-green'
                        aria-hidden='true'
                      />
                      <span className='text-sm font-normal text-brand-dark'>
                        {label}
                      </span>
                    </Link>
                    {index < navItems.length - 1 && (
                      <div className='h-px bg-black/10' aria-hidden='true' />
                    )}
                  </Fragment>
                ))}

                <div className='h-px bg-black/10' aria-hidden='true' />

                <LogoutDialog onLogout={handleLogout}>
                  <button
                    className='flex w-full items-center gap-4 rounded-sm py-4 text-left focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                    type='button'
                    aria-label='Logout from account'
                  >
                    <LogOut
                      className='size-6 text-red-500'
                      aria-hidden='true'
                    />
                    <span className='text-sm font-normal text-red-500'>
                      Logout
                    </span>
                  </button>
                </LogoutDialog>
              </nav>
            </Container>
          </div>
        </div>
      )}

      {isMobileSearchOpen && (
        <MobileSearchMenu
          isOpen={isMobileSearchOpen}
          setIsOpen={setIsMobileSearchOpen}
          isFilterOpen={isMobileSearchFilterOpen}
          setIsFilterOpen={setIsMobileSearchFilterOpen}
        />
      )}
    </NotificationProvider>
  );
}
